import { ArrowLeft, ArrowRight } from 'lucide-react'
import LocalizedLink from '@/components/layout/LocalizedLink'
import { useTranslation } from 'react-i18next'
import { resolveAssetPath } from '@/utils/basePath'
import { useUiLabels } from '@/hooks/useUiLabels'

/** زخرفة خفيفة جداً — Figma: يمين الوسط + أسفل اليسار */
function WatermarkStar({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 200 200" fill="currentColor" aria-hidden>
      <path
        opacity="0.5"
        d="M100 0 L108 40 L140 20 L120 52 L160 60 L120 68 L140 100 L108 80 L100 120 L92 80 L60 100 L80 68 L40 60 L80 52 L60 20 L92 40 Z"
      />
      <circle cx="100" cy="100" r="30" opacity="0.35" />
    </svg>
  )
}

const JOURNEY_IMAGE = '/assets/islamic_more.png'

/** مرجع Figma: أخضر أساسي */
const GREEN = '#00B36E'

/** آية قرآنية — تُعرض دائماً بالعربية (لا تُترجم مع لغة الموقع). */
const JOURNEY_SECTION_AYAH_AR =
  'وَمَن يَبْتَغِ غَيْرَ الْإِسْلَامِ دِينًا فَلَن يُقْبَلَ مِنْهُ وَهُوَ فِي الْآخِرَةِ مِنَ الْخَاسِرِينَ'
const JOURNEY_SECTION_AYAH_REF_AR = '(سورة آل عمران: 85)'

export default function JourneyToIslamCTA() {
  const { t, i18n } = useTranslation()
  const { getLabel } = useUiLabels()
  const isRTL = i18n.dir() === 'rtl'

  const featureCards = [1, 2, 3, 4].map((n) => {
    const titleKey = `home.journey.benefit${n}`
    const shortKey = `home.journey.benefit${n}Short`
    return {
      title: getLabel(titleKey, t(titleKey)),
      desc: getLabel(shortKey, t(shortKey)),
    }
  })

  return (
    <section className="bg-[#f3fcf6] py-8 dark:bg-gray-900 md:py-10">
      {/* عرض أوسع مع هامش جانبي للصفحة */}
      <div className="mx-auto w-full max-w-[min(100%,80rem)] px-4 sm:px-5 md:px-6 lg:max-w-[min(100%,88rem)] xl:max-w-[min(100%,92rem)]">
        <div
          className="relative overflow-hidden rounded-[24px] shadow-[0_4px_28px_-6px_rgba(0,80,50,0.12)] dark:shadow-black/30"
          style={{ backgroundColor: GREEN }}
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.04] to-transparent" aria-hidden />

          {/* وسط-يمين */}
          <div
            className="pointer-events-none absolute right-[6%] top-[20%] w-[13rem] text-white opacity-[0.055] md:w-[16rem] md:opacity-[0.06]"
            aria-hidden
          >
            <WatermarkStar className="h-full w-full" />
          </div>
          {/* أسفل-يسار */}
          <div
            className="pointer-events-none absolute -bottom-6 left-[-2rem] w-[11rem] text-white opacity-[0.05] md:left-[-1.5rem] md:w-[13rem]"
            aria-hidden
          >
            <WatermarkStar className="h-full w-full rotate-12" />
          </div>

          <div className="relative z-10 flex flex-col px-6 py-7 md:px-9 md:py-8 lg:px-10 lg:py-9">
            <header className="mx-auto max-w-[36rem] space-y-1 text-center text-white md:space-y-1">
              <h2 className="text-lg font-bold leading-snug tracking-tight text-white md:text-xl md:leading-tight lg:text-[1.35rem]">
                {getLabel('home.journey.title', t('home.journey.title', 'هل تريد معرفة المزيد عن الإسلام؟'))}
              </h2>
              <p className="text-[0.8125rem] font-normal leading-normal text-white/95 md:text-sm">
                {getLabel('home.journey.subtitle', t('home.journey.subtitle', 'رحلة مباركة نحو الحق والسلام الداخلي'))}
              </p>
              <p className="text-[0.7rem] font-normal leading-normal text-white/70 md:text-xs">
                {getLabel('home.journey.benefitsTitle', t('home.journey.benefitsTitle', 'ماذا ستجد في هذه الرحلة؟'))}
              </p>
            </header>

            {/* صندوق آية: عرض حسب المحتوى (w-fit) + توسيط — كالمرجع */}
            <div className="mx-auto mb-5 mt-5 w-fit max-w-full rounded-2xl border border-white/50 bg-white/20 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_0_0_1px_rgba(255,255,255,0.08)] backdrop-blur-[6px] md:mb-6 md:mt-6 md:rounded-[18px] md:px-4 md:py-2">
              <p
                className="text-center font-quran text-[0.9rem] font-normal leading-[1.75] text-white md:text-[0.95rem] md:leading-[1.8]"
                dir="rtl"
                lang="ar"
                style={{ wordSpacing: '0.08em' }}
              >
                {JOURNEY_SECTION_AYAH_AR}
              </p>
              <p
                className="mt-0.5 text-center font-arabic text-[0.625rem] font-normal leading-tight text-white/65 md:text-[0.6875rem]"
                dir="rtl"
                lang="ar"
              >
                {JOURNEY_SECTION_AYAH_REF_AR}
              </p>
            </div>

            {/* 4 كروت فقط — Figma */}
            <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:mb-6 lg:grid-cols-4 lg:gap-4">
              {featureCards.map((card, i) => (
                <div
                  key={i}
                  className="flex h-full min-h-[6.5rem] flex-col rounded-2xl border border-gray-100/90 bg-white p-3.5 shadow-[0_2px_14px_-4px_rgba(0,50,40,0.08)] dark:border-gray-700 dark:bg-gray-800 md:min-h-[7rem] md:rounded-2xl md:p-4"
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <h3
                    className={`text-[0.7rem] font-bold leading-snug text-gray-900 dark:text-gray-100 md:text-[0.75rem] ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    {card.title}
                  </h3>
                  <p
                    className={`mt-1.5 flex-1 text-[0.65rem] font-normal leading-relaxed text-gray-500 dark:text-gray-400 md:text-[0.6875rem] ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    {card.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* كارت CTA: خلفية نعناع فاتح شبه فيروزي (Figma) — الصورة ليست مباشرة عليها */}
            <div className="relative overflow-hidden rounded-[18px] border border-[#d8eee4] bg-[#ecf8f3] shadow-[0_6px_28px_-10px_rgba(0,90,55,0.12)] dark:border-gray-600 dark:bg-gray-800 md:rounded-[20px]">
              <div
                className={`pointer-events-none absolute inset-y-0 w-[55%] to-transparent dark:to-transparent ${
                  isRTL
                    ? 'right-0 bg-gradient-to-l from-emerald-50/50 dark:from-emerald-950/15'
                    : 'left-0 bg-gradient-to-r from-emerald-50/50 dark:from-emerald-950/15'
                }`}
                aria-hidden
              />
              <div
                className={`pointer-events-none absolute top-1/2 w-44 -translate-y-1/2 text-emerald-700/20 opacity-[0.09] dark:opacity-[0.06] ${
                  isRTL ? 'right-0 translate-x-2' : 'left-0 -translate-x-2'
                }`}
                aria-hidden
              >
                <WatermarkStar className="h-full w-full" />
              </div>

              <div
                className={`relative flex flex-col gap-3 px-4 py-3 md:items-stretch md:gap-4 md:px-5 md:py-0 md:pt-4 md:pb-4 lg:px-6 ${
                  isRTL ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                dir="ltr"
              >
                {/*
                  العربية: صورة يسار (flex-row) — زوايا الصورة الداخلية نحو النص يميناً.
                  الإنجليزية: صورة يمين (flex-row-reverse) — زوايا الصورة الداخلية نحو النص يساراً.
                */}
                <div className="m-0 w-full shrink-0 p-0 md:w-[38%] md:max-w-[15.5rem] md:min-w-0 md:self-stretch">
                  <div
                    className={`h-[6.5rem] w-full overflow-hidden rounded-xl sm:h-[6.75rem] md:h-full md:min-h-[7rem] md:rounded-none ${
                      isRTL ? 'md:rounded-r-2xl' : 'md:rounded-l-2xl'
                    }`}
                  >
                    <img
                      src={resolveAssetPath(JOURNEY_IMAGE)}
                      alt={getLabel(
                      'home.journey.journeyImageAlt',
                      t('home.journey.journeyImageAlt', 'صورة روحانية — رحلة معرفة الإسلام'),
                    )}
                      className="block h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>

                <div
                  className={`flex min-w-0 flex-1 flex-col gap-1.5 md:gap-2 md:justify-center ${isRTL ? 'md:pl-1 md:pr-0' : 'md:pr-1 md:pl-0'}`}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <h3
                    className="text-start text-sm font-bold leading-tight md:text-[0.9375rem]"
                    style={{ color: GREEN }}
                  >
                    {getLabel('home.journey.ctaTitle', t('home.journey.ctaTitle', 'ابدأ رحلتك الآن'))}
                  </h3>
                  <p className="max-w-[28rem] text-start text-xs font-normal leading-snug text-gray-600 dark:text-gray-300 md:text-[0.8125rem]">
                    {getLabel(
                      'home.journey.ctaDesc',
                      t(
                        'home.journey.ctaDesc',
                        'صفحة مصممة خصيصًا لتكون دليلك الروحاني نحو فهم الإسلام واعتناقه بكل سهولة ويسر',
                      ),
                    )}
                  </p>
                  <div className="flex justify-start pt-0">
                    <LocalizedLink
                      to="/journey-to-islam"
                      className="group inline-flex w-fit max-w-full items-center justify-center gap-1.5 rounded-md bg-[#00B36E] px-4 py-1.5 text-[0.6875rem] font-semibold text-white shadow-sm transition-colors hover:bg-[#009f5f] md:gap-2 md:px-4 md:py-1.5 md:text-xs"
                    >
                      <span className="text-center leading-tight">
                        {getLabel('home.journey.button', t('home.journey.button', 'انتقل إلى رحلة الدخول في الإسلام'))}
                      </span>
                      {isRTL ? (
                        <ArrowLeft className="h-3.5 w-3.5 shrink-0 opacity-95 md:h-4 md:w-4" />
                      ) : (
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-95 md:h-4 md:w-4" />
                      )}
                    </LocalizedLink>
                  </div>
                  <p className="text-start text-[0.625rem] font-normal leading-snug text-gray-400 dark:text-gray-500 md:text-[0.65rem]">
                    {getLabel('home.journey.ctaNote', t('home.journey.ctaNote', 'لا تتردد، خذ وقتك في التعلم والتفكير'))}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
